import React from 'react';
import { Button } from '@seeqdev/qomponents';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import { createRoot, Root } from 'react-dom/client';
import type { JSX } from 'react';

export type CreateFlatpickrFooterParameters = {
  flatpickrInstance: FlatpickrInstance;
};
export type CreateFlatpickrFooterReturn = (() => void) | null;
export type CreateFlatpickrFooter = <P extends CreateFlatpickrFooterParameters>(
  params: P,
) => CreateFlatpickrFooterReturn;

export const FOOTER_CSS_CLASS = 'flatpickr-footer';

export const hasFlatpickrFooter = (instance: FlatpickrInstance): boolean =>
  !!instance.calendarContainer.querySelector(`.${FOOTER_CSS_CLASS}`);

export const renderReactElementToDOM = (children: JSX.Element): [Root, HTMLDivElement] => {
  const el: HTMLDivElement = document.createElement('div');
  const root: Root = createRoot(el);
  root.render(children);
  return [root, el];
};

interface CreateFlatpickrFooterButtonsParameters extends CreateFlatpickrFooterParameters {
  t: (key: string) => string;
  onCancel: () => void;
}

export const createFooterButtons = ({
  flatpickrInstance: instance,
  t,
  onCancel,
}: CreateFlatpickrFooterButtonsParameters): CreateFlatpickrFooterReturn => {
  if (hasFlatpickrFooter(instance)) return null;

  const footer = document.createElement('div');
  footer.classList.add(FOOTER_CSS_CLASS);

  const [cancelRoot, cancelContainer] = renderReactElementToDOM(
    <Button onClick={onCancel} label={t('CANCEL')} extraClassNames="sq-btn-xs flatpickr-cancel-button" />,
  );
  const [doneRoot, doneContainer] = renderReactElementToDOM(
    <Button
      onClick={() => instance.close()}
      label={t('DONE')}
      variant="theme"
      extraClassNames="sq-btn-xs ml-1 flatpickr-done-button"
    />,
  );

  footer.appendChild(cancelContainer);
  footer.appendChild(doneContainer);
  instance.calendarContainer.appendChild(footer);

  return () => {
    try {
      cancelRoot.unmount();
      doneRoot.unmount();
    } catch (error) {
      console.warn('Error unmounting flatpickr footer buttons:', error);
    }

    // Ensure DOM cleanup even if React unmount fails
    if (footer.parentNode) {
      footer.remove();
    }
  };
};
